import React from "react";
import { AUB, INK2, LINE, avatarColor, initials } from "./chatTheme";
import { useBlobUrl } from "../../hooks/useBlobUrl";

/**
 * Schlanke, Fluent-freie UI-Primitive für den internen Chat. Personen-Avatar
 * zeigt – falls `userId` gesetzt ist – das authentifizierte Entra-Profilfoto
 * (`/users/:id/photo`, via useBlobUrl); jeder Fehler (kein Foto/Consent/Netz)
 * fällt auf die Initialen/das `label`/`icon` zurück. Teams nutzen `square` +
 * `label` (Kürzel) statt eines Fotos.
 */
export const InitialsAvatar: React.FC<{
  name: string;
  size?: number;
  square?: boolean;
  bg?: string;
  icon?: React.ReactNode;
  /** Überschreibt die Initialen (z. B. Team-Kürzel). */
  label?: string;
  /** User-Id → lädt das Entra-Profilfoto; `null`/undefined = nur Initialen. */
  userId?: string | null;
  presence?: React.ReactNode;
}> = ({ name, size = 36, square = false, bg, icon, label, userId, presence }) => {
  const photoRef = userId ? `/users/${encodeURIComponent(userId)}/photo` : null;
  const { url: photoUrl } = useBlobUrl(photoRef);
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => {
    setImgError(false);
  }, [photoUrl]);

  const background = bg ?? avatarColor(name);
  const radius = square ? Math.round(size * 0.28) : "50%";
  const showPhoto = !!photoUrl && !imgError;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {showPhoto ? (
        <img
          src={photoUrl}
          alt=""
          onError={() => setImgError(true)}
          style={{ width: size, height: size, borderRadius: radius, objectFit: "cover", display: "block" }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: size,
            height: size,
            borderRadius: radius,
            background,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: Math.round(size * (label ? 0.34 : 0.4)),
            fontWeight: 700,
            letterSpacing: 0.2,
            userSelect: "none",
          }}
        >
          {icon ?? label ?? initials(name)}
        </div>
      )}
      {presence && <span style={{ position: "absolute", bottom: -1, right: -1 }}>{presence}</span>}
    </div>
  );
};

export const MiniSpinner: React.FC<{ label?: string }> = ({ label }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: INK2, fontSize: 13 }}>
    <svg width="16" height="16" viewBox="0 0 24 24" role="img" aria-label="Lädt">
      <circle cx="12" cy="12" r="9" fill="none" stroke={LINE} strokeWidth="3" />
      <path d="M12 3 a9 9 0 0 1 9 9" fill="none" stroke={AUB} strokeWidth="3" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
    {label && <span>{label}</span>}
  </div>
);
